/**
 * A unified diff for the transcript's apply-patch view.
 *
 * Upstream renders diffs through `session-ui/src/components/file.tsx` (1 202 lines), which
 * carries virtualisation, syntax highlighting and line comments. That is out of scope here
 * — the workbench already has its own diff surface — so this produces the same shape the
 * view needs and nothing more: the changed lines, and the counts the header shows.
 *
 * The line pairing is a longest-common-subsequence, matching `workspace-changes-model.ts`.
 * A naive length comparison reports every line after an insertion as both added and
 * removed, which turns a one-line edit into a whole-file rewrite in the header.
 */

export type DiffLineKind = "context" | "added" | "removed";

export interface DiffLine {
  readonly kind: DiffLineKind;
  readonly text: string;
  /** 1-based line number in the before file, absent for an addition. */
  readonly before?: number;
  /** 1-based line number in the after file, absent for a removal. */
  readonly after?: number;
}

export interface UnifiedDiff {
  readonly lines: readonly DiffLine[];
  readonly additions: number;
  readonly deletions: number;
}

function toLines(source: string): string[] {
  if (source === "") return [];
  const split = source.split("\n");
  // A trailing newline terminates the last line rather than starting a new one.
  if (split.length > 1 && split[split.length - 1] === "") split.pop();
  return split;
}

/**
 * Full LCS table. Unlike the rolling version used for counting, backtracking needs every
 * row, so this is O(n·m) in memory — capped below.
 */
function lcsTable(left: string[], right: string[]): Uint32Array[] {
  const table: Uint32Array[] = Array.from(
    { length: left.length + 1 },
    () => new Uint32Array(right.length + 1)
  );
  for (let i = 1; i <= left.length; i += 1) {
    for (let j = 1; j <= right.length; j += 1) {
      table[i][j] =
        left[i - 1] === right[j - 1]
          ? table[i - 1][j - 1] + 1
          : Math.max(table[i - 1][j], table[i][j - 1]);
    }
  }
  return table;
}

/**
 * Above this many line pairs the LCS table costs more memory than the view is worth, so
 * the file is reported as a wholesale replacement rather than risking the tab.
 */
const MAX_PAIRS = 4_000_000;

export function unifiedDiff(before: string, after: string): UnifiedDiff {
  const left = toLines(before);
  const right = toLines(after);

  const replacement = (): UnifiedDiff => ({
    lines: [
      ...left.map((text, index) => ({ kind: "removed" as const, text, before: index + 1 })),
      ...right.map((text, index) => ({ kind: "added" as const, text, after: index + 1 }))
    ],
    additions: right.length,
    deletions: left.length
  });

  if (left.length === 0 || right.length === 0) return replacement();
  if (left.length * right.length > MAX_PAIRS) return replacement();

  const table = lcsTable(left, right);
  const lines: DiffLine[] = [];
  let additions = 0;
  let deletions = 0;

  let i = left.length;
  let j = right.length;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && left[i - 1] === right[j - 1]) {
      lines.push({ kind: "context", text: left[i - 1], before: i, after: j });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || table[i][j - 1] >= table[i - 1][j])) {
      lines.push({ kind: "added", text: right[j - 1], after: j });
      additions += 1;
      j -= 1;
    } else {
      lines.push({ kind: "removed", text: left[i - 1], before: i });
      deletions += 1;
      i -= 1;
    }
  }

  lines.reverse();
  return { lines, additions, deletions };
}

/**
 * Drops runs of unchanged lines longer than `context`, keeping the diff readable on a file
 * with one small edit. Returns the kept lines; callers render a separator where the
 * `before`/`after` numbers jump.
 */
export function collapseContext(
  diff: UnifiedDiff,
  context = 3
): readonly DiffLine[] {
  const keep = new Set<number>();
  diff.lines.forEach((line, index) => {
    if (line.kind === "context") return;
    for (let offset = -context; offset <= context; offset += 1) {
      const at = index + offset;
      if (at >= 0 && at < diff.lines.length) keep.add(at);
    }
  });
  return diff.lines.filter((_, index) => keep.has(index));
}
